import { useId, useState } from 'react';
import { generatorDraft, type Draft } from '../domain/draft.ts';
import { CFG_MAX_AMPLITUDE, PatternKind } from '../protocol/constants.ts';
import type { PatternKindValue } from '../protocol/types.ts';

export const PATTERN_LABELS: Record<PatternKindValue, string> = {
  [PatternKind.MACRO]: 'Custom',
  [PatternKind.CIRCLE]: 'Circle',
  [PatternKind.ZIGZAG]: 'Zigzag',
  [PatternKind.RANDWALK]: 'Random walk',
  [PatternKind.DRIFT]: 'Drift',
};

interface Props { draft: Draft; onChange: (draft: Draft) => void }

/** Preserve intermediate text while typing; commit a bounded wire value on blur. */
function NumberField({ label, value, min, max, step = 1, onChange }: {
  label: string; value: number; min: number; max: number; step?: number;
  onChange: (value: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const displayed = Number.isFinite(value) ? String(value) : '';
  return <label className="production__field">
    <span>{label}</span>
    <input type="number" className="num" min={min} max={max} step={step}
      value={editing ? text : displayed}
      onFocus={() => { setText(displayed); setEditing(true); }}
      onChange={(event) => {
        setEditing(true);
        setText(event.currentTarget.value);
        const next = event.currentTarget.valueAsNumber;
        // A leading 0, decimal separator or empty field is not a committed value.
        if (Number.isFinite(next) && event.currentTarget.validity.valid) onChange(next);
      }}
      onBlur={(event) => {
        const next = event.currentTarget.valueAsNumber;
        const bounded = Number.isFinite(next) ? Math.min(max, Math.max(min, Math.round(next / step) * step)) : value;
        setEditing(false);
        onChange(bounded);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
      }} />
  </label>;
}

export function PatternSettings({ draft, onChange }: Props) {
  const kindId = useId();
  function selectKind(kind: PatternKindValue) {
    const defaults = draft.kind === PatternKind.MACRO && draft.amplitude === 0 && draft.periodMs === 0
      ? generatorDraft(kind) : draft;
    // Switching away from Custom never discards its steps or changes run timing.
    onChange({ ...draft, kind, amplitude: defaults.amplitude, periodMs: defaults.periodMs, jitter: defaults.jitter });
  }
  return <section className="production__settings" aria-label="Pattern settings">
    <label className="production__field" htmlFor={kindId}>
      <span>Shape</span>
      <select id={kindId} value={draft.kind} onChange={(event) => selectKind(Number(event.target.value) as PatternKindValue)}>
        {Object.entries(PATTERN_LABELS).map(([value, name]) => <option key={value} value={value}>{name}</option>)}
      </select>
    </label>
    {draft.kind !== PatternKind.MACRO && <>
      <NumberField label="Radius (px)" value={draft.amplitude} min={0} max={CFG_MAX_AMPLITUDE}
        onChange={(amplitude) => onChange({ ...draft, amplitude: Math.round(amplitude) })} />
      <NumberField label="Cycle (s)" value={draft.periodMs / 1000} min={0.128} max={65.535} step={0.001}
        onChange={(seconds) => onChange({ ...draft, periodMs: Math.round(seconds * 1000) })} />
      <NumberField label={draft.kind === PatternKind.RANDWALK ? 'Step size (px)' : 'Variation (px)'}
        value={draft.jitter} min={0} max={255} onChange={(jitter) => onChange({ ...draft, jitter: Math.round(jitter) })} />
      <details className="production__advanced">
        <summary>More options</summary>
        <NumberField label="Seed" value={draft.seed} min={0} max={255}
          onChange={(seed) => onChange({ ...draft, seed: Math.round(seed) })} />
      </details>
    </>}
  </section>;
}

const DURATIONS = [[60, '1 minute'], [1800, '30 minutes'], [3600, '1 hour'], [28800, '8 hours'], [0, 'Until stopped']] as const;

export function RunSettings({ draft, onChange }: Props) {
  const [customSelected, setCustomSelected] = useState(false);
  const custom = customSelected || !DURATIONS.some(([seconds]) => seconds * 1000 === draft.durationMs);
  return <section className="production__run-settings" aria-label="Run settings">
    <h2>Timing</h2>
    <label className="production__field">
      <span>Run for</span>
      <select value={custom ? 'custom' : String(draft.durationMs / 1000)} onChange={(event) => {
        const value = event.target.value;
        setCustomSelected(value === 'custom');
        onChange({ ...draft, durationMs: value === 'custom' ? (draft.durationMs || 120_000) : Number(value) * 1000 });
      }}>
        {DURATIONS.map(([seconds, name]) => <option key={seconds} value={seconds}>{name}</option>)}
        <option value="custom">Custom</option>
      </select>
    </label>
    {custom && <NumberField label="Duration (s)" value={Math.ceil(draft.durationMs / 1000)} min={1} max={65535}
      onChange={(seconds) => onChange({ ...draft, durationMs: Math.round(seconds) * 1000 })} />}
    <NumberField label="Power-up delay (s)" value={draft.bootDelayMs / 1000} min={0} max={60} step={0.001}
      onChange={(seconds) => onChange({ ...draft, bootDelayMs: Math.round(seconds * 1000) })} />
    {draft.bootDelayMs === 0 && <p className="warn">Starts immediately on power-up.</p>}
  </section>;
}
